/**
 * @module app.cm
 */

import { autoinject, observable, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { ChunkedFileReader } from 'app/shared/lib/files';
import { apiCall } from 'app/shared/lib/api/api';
import { DocumentsService } from 'app/core/services/documents-service';
import { CaseService } from 'app/cm/services/case-service';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { IApiUploadRequest, ApiError, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { localSort } from 'app/shared/lib/sorting';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';

@autoinject
export class UploadDocument extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public householdCaseId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public config: IDocumentsConfig = {};

    protected cases: CM.Case[] = [];
    protected selectedCases: CM.Case[] = [];
    protected localSorting = new Sorting();

    protected category: Core.DocumentCategory | null = null;
    protected categories: Core.DocumentCategory[] = [];

    protected description: string = '';
    protected summary: string = '';
    protected progress: number = -1;

    @observable
    protected file: File | null = null;

    protected maxFileSize = 40 * 1024 * 1024;

    protected uploadError: string | null = null;

    private uploadToken: string = '';
    private uploadName: string = '';

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly documentsService: DocumentsService,
        private readonly caseService: CaseService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.householdCaseId) { throw new Error('Bindable property [household-case-id] was not set'); }
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }

        this.documentsService.configure(this.config);
        this.reload();
    }

    ////****************** Events ***************************************************////

    protected fileChanged(): void {
        if(this.file && this.file.size > this.maxFileSize) {
            this.progress = -1;
            this.uploadError = 'ui:messages.errors.upload.maximumSizeExceeded';
            return;
        }
        this.uploadFile();
    }

    ////****************** Functions ************************************************////

    private async reload(): Promise<void> {
        await this.loadLookups();
    }

    private async uploadFile(): Promise<void> {
        if(!this.file) { return; }

        const file = this.file;
        const reader = new ChunkedFileReader(file);
        try {
            this.state.beginUpload();
            this.uploadError = null;
            this.progress = -1;
            this.uploadToken = '';

            let request: IApiUploadRequest = {
                file: {
                    contentType: file.type || undefined,
                    name: file.name
                }
            };
            while(!reader.isComplete()) {
                const base64 = await reader.readBase64();

                request.contents = { data: [base64], encoding: 'base64' };
                const response = await this.documentsService.uploadFile(request);
                if(!response.token || !response.token.value) {
                    break;
                }

                request.token = { value: response.token.value }
                delete request.file;

                this.progress = reader.calculateProgress();
            }

            if(request.token) {
                this.uploadToken = request.token.value;
                this.uploadName = file.name;
            }
        }
        catch(error) {
            let uploadError: string = 'ui:messages.errors.upload.$';
            if(error instanceof ApiError) {
                if(error.hasError('upload.unsupportedMediaType')) {
                    uploadError = 'ui:messages.errors.upload.unsupportedMediaType';
                }
            }
            this.uploadError = uploadError;
            this.progress = -1;
            this.uploadToken = '';

            throw error;
        }
        finally {
            this.state.endUpload();
        }
    }

    protected buildDocument(): CM.CaseDocument {
        const saveDocument: CM.CaseDocument = <any>{
            category: getReference(this.category),
            description: this.description || undefined,
            summary: this.summary || undefined,
            file: { name: this.uploadName },
            upload: { token: { value: this.uploadToken } },
            cases: this.selectedCases.map(c => getReference(c))
        };

        return saveDocument;
    }

    ////****************** IForm ****************************************************////

    public hasChanges(): boolean {
        return false;
    }

    public async submit(): Promise<boolean> {
        return await this.addDocument();
    }

    public reset(): void {
        this.selectedCases.splice(0);
        this.category = null;
        this.uploadToken = '';
        this.description = '';
        this.summary = '';
        this.file = null;
        this.progress = -1;
        this.uploadError = null;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadLookups(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = { all: true };
        const caseIncludes: string[] = ['member'];

        try {
            this.state.beginLoad();

            const [cases, categories] = await Promise.all(
                [
                    this.caseService.loadCasesByHouseholdCase(this.householdCaseId, options, caseIncludes),
                    this.documentsService.loadCategories()
                ]
            );

            this.categories = categories.results;

            this.cases = cases.results;
            // use local sorting to sort cases by member description
            this.localSorting.entries = [
                {
                    reference: 'member.description',
                    direction: SortDirection.Ascending
                }
            ];
            localSort(this.cases, this.localSorting);
        } catch(error) {
            this.logger.error('Error Loading Lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async addDocument(): Promise<boolean> {
        let success = false;
        try {
            this.state.beginSave();

            const response = await this.documentsService.attachDocument(this.buildDocument());
            success = response.isSuccessful;

            return success;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('core:document.$', success);
            this.state.endSave();
        }
    }

}
